import * as React from 'react'
import {
  usePreviewTabularDumpMutation,
  type TabularDumpIssue,
  type TabularDumpTablePreview
} from '@axilon-apps/domains-ontology-ui-state'
import { Loader2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table'

function isAbortError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const record = error as Record<string, unknown>
  if (record.name === 'AbortError') return true
  const text = [record.error, record.message, record.status]
    .filter((value) => typeof value === 'string')
    .join(' ')
  return /abort/i.test(text)
}

function previewErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const record = error as Record<string, unknown>
    if (typeof record.data === 'string' && record.data.trim()) return record.data
    if (record.data && typeof record.data === 'object') {
      const data = record.data as Record<string, unknown>
      if (typeof data.error === 'string' && data.error.trim()) return data.error
      if (typeof data.message === 'string' && data.message.trim()) return data.message
    }
    if (typeof record.error === 'string' && record.error.trim()) return record.error
    if (typeof record.message === 'string' && record.message.trim()) return record.message
  }
  return 'Could not preview this dump.'
}

export function DumpFilePreview({ file }: { file: File }) {
  const [previewTabularDump, { data, isError, error }] = usePreviewTabularDumpMutation()

  React.useEffect(() => {
    const request = previewTabularDump({ file })
    return () => {
      request.abort()
    }
  }, [file, previewTabularDump])

  if (isError && !isAbortError(error)) {
    return (
      <p role="alert" className="text-xs text-st-down">
        {previewErrorMessage(error)}
      </p>
    )
  }

  if (!data) {
    return (
      <div role="status" className="flex items-center gap-2 text-xs text-ink-3">
        <Loader2 className="size-3.5 shrink-0 animate-spin" />
        Reading tables from {file.name}…
      </div>
    )
  }

  return <DumpPreviewTables tables={data.tables} issues={data.issues} />
}

export function DumpPreviewTables({
  tables,
  issues
}: {
  tables: TabularDumpTablePreview[]
  issues: TabularDumpIssue[]
}) {
  return (
    <div className="flex flex-col gap-2.5">
      {issues.map((issue) => (
        <p
          key={`${issue.sourceName}:${issue.message}`}
          role={issue.severity === 'error' ? 'alert' : 'status'}
          className={issue.severity === 'error' ? 'text-xs text-st-down' : 'text-xs text-st-warn'}
        >
          {issue.sourceName}: {issue.message}
        </p>
      ))}
      {tables.map((table) => (
        <DumpPreviewTable key={table.tableName} table={table} />
      ))}
    </div>
  )
}

function DumpPreviewTable({ table }: { table: TabularDumpTablePreview }) {
  const samples = table.sampleRows ?? []
  return (
    <section className="overflow-hidden rounded-md border border-line-2 bg-bg-sunk">
      <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line-2 px-2.5 py-1.5">
        <div className="min-w-0">
          <h3 className="truncate font-mono text-xs font-medium text-ink">{table.tableName}</h3>
          {table.sourceName !== table.tableName ? (
            <p className="truncate text-10 text-ink-3">Sheet {table.sourceName}</p>
          ) : null}
        </div>
        <p className="shrink-0 text-10 text-ink-3">
          {table.rowCount} row{table.rowCount === 1 ? '' : 's'}
        </p>
      </header>
      <Table className="text-11">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {table.columns.map((column, index) => (
              <TableHead key={`${column}:${index}`} className="h-7 px-2 font-mono text-ink-2">
                {column}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {samples.map((row, rowIndex) => (
            <TableRow key={rowIndex} className="hover:bg-transparent">
              {table.columns.map((column, columnIndex) => (
                <TableCell
                  key={`${column}:${columnIndex}`}
                  className="px-2 py-1 font-mono text-ink-2"
                >
                  {row[columnIndex] ?? ''}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  )
}
